import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../Common/Button";
import * as yup from "yup";
import { Formik } from "formik";
import { userLogin } from "../../utils/authService";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import { InputGroup, Form } from "react-bootstrap";
import { getToken, setToken } from "../../utils/tokenStorage";
import { consumeStudentRedirectUrl } from "../../utils/redirectStorage";
import { useAppContext } from "../../AppContext";
import { useDispatch } from "react-redux";
import LazyLoad from "react-lazyload";
import {
  getUserInformation,
  setUserInformation,
  setUserPermissions,
  setUserRole,
} from "../../store/userRole";
import "./styles.scss";
import { clearSubscription } from "../../store/subscriptionSlice";
import { fetchCategories } from "../../store/categoriesSlice";
import { getLiveClassDataById, joinLiveClass } from "../../utils/liveClasses";
import Loader from "../Loader";
import OtpComponent from "./OtpComponent"; // Import the OTP component
import { loginRequest } from "../../utils/api";
import { useSelector } from "react-redux";
import { openLinkInNewTab } from "../../pages/StudentPages/LiveClasses/utils";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

export const getDeviceId = async () => {
  const uniqueId = localStorage.getItem("dmapid");
  if (uniqueId) {
    return uniqueId;
  }
  const fp = await FingerprintJS.load();
  const result = await fp.get();
  return result.visitorId; // Unique device hash
};

const loginSchema = yup.object({
  phone: yup.string().when("loginMethod", {
    is: "phone",
    then: yup.string().required("Phone number is required"),
  }),
  email: yup.string().when("loginMethod", {
    is: "email",
    then: yup
      .string()
      .email("Invalid email address")
      .required("Email is required"),
  }),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
});

const LoginForm = ({ setForgotPassword, forgotPassword }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState("phone"); // 'phone' or 'email'
  const [showOtp, setShowOtp] = useState(false); // Control OTP component visibility
  const [emailForOtp, setEmailForOtp] = useState(""); // Store email for OTP verification
  const [passwordForOtp, setPasswordForOtp] = useState(""); // Store password to pass to OTP component
  const [purpose, setPurpose] = useState("login");
  const justLoggedInRef = useRef(false); // Use ref for immediate updates (no re-render delay)
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch } = useAppContext();
  const storeDispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const liveclassid = queryParams.get("liveclassid");
  const classroomId = queryParams.get("classroomId");
  const redirectUrl = queryParams.get("redirect"); // Extract redirect URL from query params
  const userInformation = useSelector(getUserInformation); // Get user information

  useEffect(() => {
    const checkAuthenticationAndNavigate = async () => {
      try {
        const token = await getToken();
        const isAuthenticated = token !== null;

        if (isAuthenticated && userInformation) {
          if (liveclassid) {
            joinLiveClassDirectly(liveclassid);
          } else {
            // For already authenticated users, don't navigate here - let the login handler manage it
            // This prevents double navigation for new logins
            if (justLoggedInRef.current) {
              return;
            }
          }
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
      }
    };

    checkAuthenticationAndNavigate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInformation, liveclassid]);

  const joinLiveClassDirectly = async (liveclassid, formattedData) => {
    setLoading(true);
    try {
      const liveClassResponse = await getLiveClassDataById(liveclassid);
      if (liveClassResponse.ok) {
        const { meetingID, attendeePW } = liveClassResponse.data.liveClass;
        const joinResponse = await joinLiveClass(
          liveclassid,
          JSON.stringify({
            meetingID,
            password: attendeePW,
            classroomId: classroomId,
          }),
          "join",
        );
        const loginResponse = await userLogin(formattedData);
        if (loginResponse.ok && loginResponse.data.token) {
          storeDispatch(setUserInformation(loginResponse.data.user));
          storeDispatch(
            setUserPermissions(loginResponse.data.user.permissions),
          );
        }
        if (joinResponse.ok && joinResponse.data.url) {
          openLinkInNewTab(joinResponse.data.url);
          navigate(`/student/myClassrooms/${classroomId}/liveclasses`); // Navigate to dashboard after joining the class
        } else {
          navigate(`/student/myClassrooms/${classroomId}/liveclasses`); // Navigate to dashboard after joining the class
          // toast.error("Failed to join live class.");
        }
      } else {
        toast.error("Failed to fetch live class details.");
      }
    } catch (error) {
      console.error("Error joining live class:", error);
      navigate(`/student/myClassrooms/${classroomId}/liveclasses`); // Navigate to dashboard after joining the class
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (data) => {
    setLoading(true);
    try {
      const formattedData =
        loginMethod === "phone"
          ? { ...data, phone: `+${data.phone}` }
          : { ...data, email: data.email };
      const deviceId = await getDeviceId();
      const loginResponse = await loginRequest({
        ...formattedData,
        deviceId,
      });
      if (loginResponse.ok && loginResponse.data.token) {
        // Handle successful login
        // Mark that user just logged in BEFORE setting user info to prevent useEffect from interfering
        justLoggedInRef.current = true;

        localStorage.setItem("dmapid", deviceId);
        setToken(loginResponse.data.token);
        const roleOfUser = loginResponse.data.user.roleName;
        storeDispatch(setUserRole(roleOfUser));
        storeDispatch(setUserInformation(loginResponse.data.user));
        storeDispatch(setUserPermissions(loginResponse.data.user.permissions));
        dispatch({ type: "SET_AUTH_STATUS", payload: true });
        dispatch(fetchCategories());
        storeDispatch(clearSubscription());

        // Navigate immediately after successful login
        if (liveclassid) {
          await joinLiveClassDirectly(liveclassid, formattedData);
        } else {
          // Define admin/teacher roles that should go to admin interface
          const adminRoles = [
            "Admin",
            "SuperAdmin",
            "Root",
            "Teacher",
            "Watcher",
          ];
          const isAdminUser =
            adminRoles.includes(roleOfUser) || roleOfUser !== "Student";

          if (isAdminUser) {
            // For non-students (admin/teacher users), clear any stored student redirect URL and use default route
            consumeStudentRedirectUrl(); // Clear the stored URL
            navigate("/myClassrooms", { replace: true });
          } else {
            // For students, check for stored redirect URL first, then URL params
            // Mark login in progress for PublicRoute to detect
            sessionStorage.setItem("login-in-progress", "true");

            const storedRedirectUrl = consumeStudentRedirectUrl();

            if (storedRedirectUrl) {
              navigate(storedRedirectUrl, { replace: true });
            } else if (redirectUrl !== null) {
              const fullRedirectUrl = `/${redirectUrl}`;
              navigate(fullRedirectUrl, { replace: true });
            } else {
              navigate("/student/myClassrooms", { replace: true });
            }
          }
        }

        toast.success("Login successful!");
      } else if (
        loginResponse?.data?.message &&
        loginResponse?.data?.message.includes("OTP")
      ) {
        // If the response message includes "OTP", show the OTP component
        setEmailForOtp(
          loginMethod === "phone" ? `+${data.phone}` : data?.email,
        ); // Save the email for OTP verification
        setPasswordForOtp(data?.password); // Save the password for OTP verification
        setPurpose("login");
        setShowOtp(true); // Display the OTP component
        toast.info("Please verify with the OTP sent to your email.");
      } else {
        toast.error(loginResponse.error || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    await handleLogin(values);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        const submitButton = document.getElementById("login-submit-button");
        if (submitButton) {
          submitButton.click();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <>
      <Formik
        initialValues={{ phone: "", password: "" }}
        validationSchema={loginSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          handleBlur,
          setFieldValue,
        }) => (
          <div className="right-side">
            {loading && <Loader />}
            {showOtp ? (
              <OtpComponent
                email={emailForOtp} // Pass the stored email to OtpComponent
                password={passwordForOtp} // Pass the stored password to OtpComponent
                liveclassid={liveclassid}
                setShowOtp={setShowOtp}
                purpose={purpose}
                onSuccess={(userRole) => {
                  setShowOtp(false);
                  // Role-based redirect after OTP verification
                  const adminRoles = [
                    "Admin",
                    "SuperAdmin",
                    "Root",
                    "Teacher",
                    "Watcher",
                  ];
                  const isAdminUser =
                    adminRoles.includes(userRole) || userRole !== "Student";

                  if (isAdminUser) {
                    navigate("/myClassrooms", { replace: true });
                  } else {
                    navigate("/student/myClassrooms", { replace: true });
                  }
                }}
                loginMethod={loginMethod}
              />
            ) : (
              <form className="w-100" onSubmit={handleSubmit}>
                <div className="login-heading">
                  <h3>Login</h3>
                </div>
                <div className="login-method-tabs">
                  <div
                    className={`login-tab ${
                      loginMethod === "phone" ? "active" : ""
                    }`}
                    onClick={() => {
                      setLoginMethod("phone");
                      setFieldValue("email", ""); // Clear email field when switching to phone
                    }}
                  >
                    Phone
                  </div>
                  <div
                    className={`login-tab ${
                      loginMethod === "email" ? "active" : ""
                    }`}
                    onClick={() => {
                      setLoginMethod("email");
                      setFieldValue("phone", ""); // Clear phone field when switching to email
                    }}
                  >
                    Email
                  </div>
                </div>
                <div className="w-100">
                  {loginMethod === "phone" ? (
                    <>
                      <label htmlFor="phone">Phone Number</label>
                      <PhoneInput
                        country={"us"}
                        value={values.phone}
                        onChange={(phone) => setFieldValue("phone", phone)}
                        type="tel"
                        name="phone"
                        excludeCountries={[
                          "by", // Belarus
                          "ly", // Libya
                          "cd", // Democratic Republic of Congo
                          "cg", // Republic of Congo
                          "ye", // Yemen
                          "ss", // South Sudan
                          "so", // Somalia
                          "sd", // Sudan
                          "cf", // Central African Republic
                          "cu", // Cuba
                          "kp", // North Korea
                          "ir", // Iran
                          "sy", // Syria
                        ]}
                      />
                      <p className="text-danger">
                        {errors.phone && touched.phone && errors.phone}
                      </p>
                    </>
                  ) : (
                    <>
                      <label htmlFor="email">Email</label>
                      <Form.Control
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <p className="text-danger">
                        {errors.email && touched.email && errors.email}
                      </p>
                    </>
                  )}
                </div>
                <div className="w-100">
                  <Form.Group controlId="password">
                    <Form.Label>Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <InputGroup.Text style={{ background: "transparent" }}>
                        <div
                          className="password-toggle-icon"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <LazyLoad height={20} offset={100}>
                            <img
                              src={
                                showPassword
                                  ? "password-eye-slash.svg"
                                  : "password-eye.svg"
                              }
                              alt="showPassword"
                            />
                          </LazyLoad>
                        </div>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                  <p className="text-danger">
                    {errors.password && touched.password && errors.password}
                  </p>
                </div>
                <div className="w-100 d-flex align-items-center justify-content-between mt-3">
                  <p
                    className="forgot-password-link"
                    onClick={() => setForgotPassword(!forgotPassword)}
                  >
                    Forgot Password?
                  </p>
                  <Button
                    id="login-submit-button" // Assign ID here
                    disabled={loading}
                    className="login-btn"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <span>{loading ? "Logging in..." : "Sign In"}</span>
                    <LazyLoad height={20} offset={100}>
                      <img src="/ArrowRight.png" alt="arrow" />
                    </LazyLoad>
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </Formik>
    </>
  );
};

export default LoginForm;
